import { Janus } from 'janus-gateway'
import { EventEmitter } from 'events'
import { initJanus, createSession } from './janus'
import { v4 as uuidv4 } from 'uuid'
import {
  UserDisplayInfo,
  WebRTCProviderType,
  Participant,
  RoomInfo
} from './types'
import {
  WebRTCEventTypes,
  WebRTCProviderEventTypes
} from './constants/eventTypes'
import { decamelizeKeys, getEnvVar } from '@/utils'
import store from '@/store'

export { WebRTCEventTypes }

export const genRoomId = () => uuidv4()
export const genPin = () => uuidv4()
export const genRoomSecret = () => uuidv4()
export const attachMediaStream = (
  element: HTMLVideoElement | HTMLAudioElement,
  stream: MediaStream
) => Janus.attachMediaStream(element, stream)

const ROOM_EXISTS_ERROR = 427
const RETRY_COUNT_LIMIT = 3

let intialized = false

export class WebRTC {
  readonly SERVER_ENDPOINT = [getEnvVar('WEBRTC_ENDPOINT')]

  roomId = ''
  pinCode = ''
  roomSecret = ''
  defaultVideoEnable = true

  localHandle: any = null
  remoteHandles: { [id: number]: any } = {}
  session: any = null

  events = new EventEmitter()

  shareScreenActivated = false
  shareScreenPending = false

  static async init() {
    if (intialized) {
      return
    }

    await initJanus()

    intialized = true
  }

  constructor(
    apiSecret: string,
    roomId = '',
    pinCode = '',
    roomSecret = '',
    defaultVideoEnable = true
  ) {
    if (!intialized) {
      throw new Error('Constructor cannot be called before initialized')
    }
    if (!this.isWebrtcSupported()) {
      throw new Error('WebRTC is not supported')
    }

    this.roomId = roomId
    this.pinCode = pinCode
    this.roomSecret = roomSecret
    this.defaultVideoEnable = defaultVideoEnable

    this.session = createSession(this.SERVER_ENDPOINT, apiSecret, this)

    this.observe(
      WebRTCEventTypes.SESSION_SUCCEEDED,
      (localHandle: any) => (this.localHandle = localHandle)
    )
    this.observe(
      WebRTCEventTypes.REMOTE_JOINED,
      this.handleRemoteJoined.bind(this)
    )
    this.observe(
      WebRTCEventTypes.MEDIA_STATE_CHANGED,
      this.handleMediaStateChanged.bind(this)
    )
    this.observe(
      WebRTCEventTypes.REMOTE_LEAVED,
      this.handleRemoteLeaved.bind(this)
    )
  }

  isWebrtcSupported() {
    return Janus.isWebrtcSupported()
  }

  observe(type: WebRTCEventTypes, observer: (...args: any[]) => any) {
    this.events.on(type, observer)

    return () => {
      this.events.off(type, observer)
    }
  }

  async createRoom(retry = 0): Promise<RoomInfo> {
    try {
      const roomId = genRoomId()
      const pinCode = genPin()
      const roomSecret = genRoomSecret()

      const results = new Promise<RoomInfo>((resolve, reject) => {
        this.localHandle.send({
          message: {
            request: 'create',
            permanent: false,
            secret: roomSecret,
            room: roomId,
            pin: pinCode,
            bitrate: 256000,
            // eslint-disable-next-line @typescript-eslint/camelcase
            bitrate_cap: true,
            publishers: 8
          },
          success: (response: any) => {
            if (!response.error) {
              this.roomId = roomId
              this.pinCode = pinCode
              this.roomSecret = roomSecret

              const roomInfo = { roomSecret, roomId, pinCode }

              this.events.emit(WebRTCEventTypes.ROOM_CREATED, roomInfo)

              resolve(roomInfo)
            } else {
              reject(response)
            }
          },
          error: (error: any) => {
            reject(error)
          }
        })
      })

      return await results
    } catch (error) {
      console.error(error)

      if (retry > RETRY_COUNT_LIMIT) {
        console.error('retry count is exceeded')
        throw error
      }

      if (error.error_code === ROOM_EXISTS_ERROR) {
        return await this.createRoom(++retry)
      }

      throw error
    }
  }

  destroyRoom(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.localHandle.send({
        message: {
          request: 'destroy',
          room: this.roomId,
          secret: this.roomSecret,
          permanent: false
        },
        success: (response: any) => {
          resolve(response)
        },
        error: (error: any) => {
          reject(error)
        }
      })
    })
  }

  publish(userId: number, displayInfo: UserDisplayInfo) {
    const message = decamelizeKeys({
      request: 'join',
      room: this.roomId,
      pin: this.pinCode,
      isPrivate: true,
      ptype: 'publisher',
      display: JSON.stringify(displayInfo),
      id: String(userId)
    })
    this.localHandle.send({ message })
  }

  async destroy() {
    try {
      const participants = await this.fetchParticipantList()

      const cleanup = () => {
        this.session.destroy()
        this.localHandle.detach()
        Object.keys(this.remoteHandles).forEach(id =>
          this.remoteHandles[Number(id)].detach()
        )
      }

      if (Array.isArray(participants) && participants.length > 1) {
        cleanup()
        return
      }

      await this.destroyRoom()

      cleanup()
    } catch (error) {
      //
    }
  }

  fetchParticipantList(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.localHandle.send({
        message: {
          request: 'listparticipants',
          room: this.roomId
        },
        success: (response: any) => {
          if (!response.error) {
            resolve(response.participants as Array<any>)
          } else {
            reject(response)
          }
        }
      })
    })
  }

  switchAudio(value: boolean) {
    const videoActive = store.getters['videoCallRoom/videoActive']

    this.localHandle.createOffer({
      media: {
        video: true,
        replaceVideo: false,
        replaceAudio: true,
        audioSend: value,
        videoSend: videoActive
      },
      success: (jsep: any) => {
        this.shareScreenPending = false
        const message = {
          request: 'configure',
          audio: value,
          video: videoActive
        }
        this.localHandle.send({ message, jsep })
      },
      error: (error: any) => {
        console.error('WebRTC error:', error)
      }
    })
  }

  switchVideo(value: boolean) {
    // 画面共有中はシグナルの変更は送らない。
    // 画面共有の解除時に現在のミュート設定が適用される。
    if (store.getters['videoCallRoom/shareScreenActive']) {
      return
    }

    const microphoneActive = store.getters['videoCallRoom/microphoneActive']

    this.localHandle.createOffer({
      media: {
        video: true,
        replaceVideo: true,
        audioSend: microphoneActive,
        videoSend: value
      },
      success: (jsep: any) => {
        this.shareScreenPending = false
        const message = {
          request: 'configure',
          audio: microphoneActive,
          video: value
        }
        this.localHandle.send({ message, jsep })
      },
      error: (error: any) => {
        this.shareScreenPending = false

        if (error instanceof DOMException) {
          if (error.message.indexOf('Permission denied') > -1) {
            // ダイアログキャンセルの処理
            this.switchShareScreen(!value)
            this.events.emit(WebRTCEventTypes.FORCE_SHARE_SCREEN_CHANGE, !value)
          }
        }
        console.error('WebRTC error:', error)
      }
    })
  }

  switchShareScreen(value: boolean) {
    if (this.shareScreenPending) {
      return
    }

    this.shareScreenPending = true

    const capture = value ? 'screen' : true
    // 画面共有する場合無条件でビデオ信号を送る。
    // 画面共有以外はユーザーの設定に従う
    const isSendVideo = value
      ? true
      : store.getters['videoCallRoom/videoActive']
    const microphoneActive = store.getters['videoCallRoom/microphoneActive']

    this.localHandle.createOffer({
      media: {
        video: capture,
        replaceVideo: true,
        audioSend: microphoneActive,
        videoSend: true
      },
      success: (jsep: any) => {
        this.shareScreenPending = false
        const message = {
          request: 'configure',
          audio: microphoneActive,
          video: isSendVideo
        }
        this.localHandle.send({ message, jsep })
        this.shareScreenActivated = value
      },
      error: (error: any) => {
        this.shareScreenPending = false

        if (error instanceof DOMException) {
          if (error.message.indexOf('Permission denied') > -1) {
            // ダイアログキャンセルの処理
            this.switchShareScreen(!value)
            this.events.emit(WebRTCEventTypes.FORCE_SHARE_SCREEN_CHANGE, !value)
          }
        }

        console.error('WebRTC error:', error)

        this.events.emit(
          WebRTCEventTypes.SHARE_SCREEN_FIXED,
          this.shareScreenActivated
        )
      }
    })
  }

  setInputDevice(type: 'audio' | 'video', deviceId: string) {
    const replacePropertyName = `replace${type
      .slice(0, 1)
      .toUpperCase()}${type.slice(1)}`

    const microphoneActive = store.getters['videoCallRoom/microphoneActive']
    const videoActive = store.getters['videoCallRoom/videoActive']

    return new Promise((resolve, reject) => {
      this.localHandle.createOffer({
        media: {
          [type]: { deviceId },
          [replacePropertyName]: true,
          audioSend: microphoneActive,
          videoSend: videoActive
        },
        success: (jsep: any) => {
          const message = { request: 'configure', audio: true, video: true }
          this.localHandle.send({ message, jsep })
          resolve()
        },
        error: (error: any) => {
          reject(error)
        }
      })
    })
  }

  handleRemoteJoined(handle: any, participant: Participant) {
    this.remoteHandles[participant.id as number] = handle
  }

  handleRemoteLeaved(id: number) {
    this.remoteHandles[id].detach()
    delete this.remoteHandles[id]
  }

  handleMediaStateChanged(mediaType: string, state: boolean) {
    if (this.shareScreenActivated && !this.shareScreenPending) {
      if (mediaType === 'video' && state === false) {
        if (this.shareScreenPending) {
          const leave = this.observe(
            WebRTCEventTypes.SHARE_SCREEN_FIXED,
            () => {
              this.switchShareScreen(false)
              this.events.emit(
                WebRTCEventTypes.FORCE_SHARE_SCREEN_CHANGE,
                false
              )
              leave()
            }
          )
          return
        }
        this.switchShareScreen(false)
        this.events.emit(WebRTCEventTypes.FORCE_SHARE_SCREEN_CHANGE, false)
      }
    }
  }
}

const WebRTCProvider: WebRTCProviderType = {
  events: new EventEmitter(),
  connection: null,

  async createConnection(
    apiSecret,
    roomId = '',
    pinCode = '',
    roomSecret = '',
    defaultVideoEnable = true
  ) {
    if (!intialized) {
      await WebRTC.init()
    }

    this.connection = new WebRTC(
      apiSecret,
      roomId,
      pinCode,
      roomSecret,
      defaultVideoEnable
    )

    this.events.emit(WebRTCProviderEventTypes.WEB_RTC_CREATED, this.connection)

    return this.connection
  },

  getConnection() {
    return this.connection
  },

  destroy() {
    if (this.connection) {
      this.connection.destroy()
    }
    this.connection = null
  },

  observeOnce(type, observer) {
    this.events.once(type, observer)

    return () => this.events.off(type, observer)
  }
}

export default WebRTCProvider
